import { unit } from '@ant-design/cssinjs';
import type { CSSObject } from '@ant-design/cssinjs';

import { operationUnit } from '../../style';
import type { GenerateStyle } from '../../theme/internal';
import type { TableToken } from './index';

/**
 * Offsets a second level table inside the expanded row cell so that it keeps the
 * `nestedTablePaddingBlock*` gap to the light blue container and lines its first
 * column up with the first column of the parent table.
 *
 * The offsets are relative to the padding the cell already carries, which is not
 * symmetric: the default size zeroes `paddingBlock` on body cells while the
 * `middle` / `small` sizes re-apply it — so callers pass the cell's real padding.
 */
export const genNestedTableOffset = (
  token: TableToken,
  cellPaddingBlock: number,
  paddingHorizontal: number,
  expandColumnWidth: number | string,
): CSSObject => {
  const { nestedTableMaxWidth, nestedTablePaddingBlockStart, nestedTablePaddingBlockEnd, calc } =
    token;

  return {
    maxWidth: nestedTableMaxWidth,
    marginBlockStart: unit(calc(nestedTablePaddingBlockStart).sub(cellPaddingBlock).equal()),
    marginBlockEnd: unit(calc(nestedTablePaddingBlockEnd).sub(cellPaddingBlock).equal()),
    marginInlineStart: unit(calc(expandColumnWidth).sub(paddingHorizontal).equal()),
    marginInlineEnd: unit(calc(paddingHorizontal).mul(-1).equal()),
  };
};

const genExpandStyle: GenerateStyle<TableToken, CSSObject> = (token) => {
  const {
    componentCls,
    antCls,
    paddingXS,
    tableExpandColumnWidth,
    tableExpandIconBg,
    tablePaddingVertical,
    tablePaddingHorizontal,
    tableExpandedRowBg,
    rowExpandedParentBg,
    expandIconColor,
    expandIconSize,
    calc,
  } = token;

  return {
    [`${componentCls}-wrapper`]: {
      [`${componentCls}-expand-icon-col`]: {
        width: tableExpandColumnWidth,
      },

      // A column of its own: centre the icon on both axes.
      [`${componentCls}-row-expand-icon-cell`]: {
        textAlign: 'center',

        [`${componentCls}-row-expand-icon`]: {
          float: 'none',
          verticalAlign: 'middle',
        },
      },

      [`${componentCls}-row-indent`]: {
        height: 1,
        float: 'left',
      },

      // The design draws this as a plain product icon (`Add` / `Minus`) with no
      // framing box, so the button only centres the icon and carries the colour.
      // Flex centring places the glyph, so no baseline nudge is needed — the old
      // CSS-drawn mark in a box relied on one.
      [`${componentCls}-row-expand-icon`]: {
        ...operationUnit(token),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        float: 'left',
        width: expandIconSize,
        height: expandIconSize,
        color: expandIconColor,
        fontSize: expandIconSize,
        lineHeight: 1,
        verticalAlign: 'middle',
        background: tableExpandIconBg,
        border: 0,

        '&:focus, &:hover, &:active': {
          color: expandIconColor,
        },

        '&-spaced': {
          visibility: 'hidden',
        },
      },

      // Sharing a cell with the row's text (tree data): keep it on the text's
      // centre line rather than floating it to the top of the line box.
      [`${componentCls}-row-indent + ${componentCls}-row-expand-icon`]: {
        float: 'none',
        verticalAlign: 'middle',
        marginInlineEnd: paddingXS,
      },

      // ===================== Expanded parent row ======================
      // The row owning an open second level table shares the container background,
      // matching the `Expanded` usage of the Table Cell Content component.
      [`${componentCls}-tbody > tr${componentCls}-row${componentCls}-row-expanded-parent`]: {
        [`> ${componentCls}-cell, > ${componentCls}-cell-row-hover`]: {
          background: rowExpandedParentBg,
          // The design draws this divider in the container colour, so it reads as
          // no divider at all while the row keeps its regular height.
          borderBottomColor: rowExpandedParentBg,
        },
      },

      // The design has no divider between the second level container and the row
      // that follows it either, which is also what makes the gap below the second
      // level table exactly `nestedTablePaddingBlockEnd`.
      [`${componentCls}-tbody > tr${componentCls}-expanded-row`]: {
        '> th, > td': {
          borderBottom: 0,
        },
      },

      [`tr${componentCls}-expanded-row`]: {
        '&, &:hover': {
          '> th, > td': {
            background: tableExpandedRowBg,
          },
        },

        // https://github.com/ant-design/ant-design/issues/25573
        [`${antCls}-descriptions-view`]: {
          display: 'flex',

          table: {
            flex: 'auto',
            width: '100%',
          },
        },
      },

      // With fixed
      [`${componentCls}-expanded-row-fixed`]: {
        position: 'relative',
        margin: `${unit(calc(tablePaddingVertical).mul(-1).equal())} ${unit(
          calc(tablePaddingHorizontal).mul(-1).equal(),
        )}`,
        padding: `${unit(tablePaddingVertical)} ${unit(tablePaddingHorizontal)}`,
      },
    },
  };
};

export default genExpandStyle;
